import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { faHome, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICorporationResponse, ILocationResponse } from '@kcftech/prometheus-client';
import useResizeObserver from '@react-hook/resize-observer';
import { HoverIconButton } from 'components/button/HoverIconButton';
import {
    CorporationAbbreviation,
    CorporationDropdown,
} from 'components/dropdown/CorporationDropdown';
import { LocationDropdown } from 'components/dropdown/LocationDropdown';
import { GlobalSearch } from 'components/global_search/GlobalSearch';
import { useNavigationDrawerValues } from 'components/navigation_drawer/navigation_drawer_store';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { CorporationLogoButton } from 'components/top_bar/CorporationLogoButton';
import { EnvironmentSwitcherPopup } from 'components/top_bar/EnvironmentSwitcherPopup';
import { FavoritesPopup } from 'components/top_bar/FavoritesPopup';
import { QaUserSwitcherPopup } from 'components/top_bar/QaUserSwitcherPopup';
import { TopBarThemePicker } from 'components/top_bar/TopBarThemePicker';
import { UserPopup } from 'components/top_bar/UserPopup';
import { useScopeNavigation } from 'components/top_bar/useScopeNavigation';
import { usePermissions } from 'contexts/PermissionsContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { hasAccessToMultipleCorporations, useUser } from 'contexts/UserContext';
import {
    ShortcutDescription,
    useGlobalSearchVisibility,
} from 'custom_hooks/useGlobalSearchVisibility';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { MissionControlEntityNav } from 'pages/MissionControl/MissionControlEntityNav';
import { memo, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { DebugToggle } from 'shared/debug/DebugToggle';
import styled, { css } from 'styled-components';
import { MenuButton, MenuLink } from 'styles/theme_styles/button';
import { tokens } from 'styles/tokens';
import { useIsDashboardView, useIsMissionControl } from 'utils/RouteHelper';

const TopBarWrapper = styled.nav`
    background: ${tokens.background.paperElevation2};
    color: ${tokens.text.primary};
    border-bottom: 1px solid ${tokens.border.default};
`;

const TopBarSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 100%;
`;

const LocationDropdownLabel = styled.span`
    display: -webkit-box;
    // both of these webkit props have over 97% browser support.
    -webkit-box-orient: vertical;
    box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
`;

const dropdownStyles = css`
    font-size: 1.4rem;
    color: ${tokens.text.secondary};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    transition: background-color color 0.075s;
    &:hover {
        background-color: var(--action-hover) !important;
        color: ${tokens.text.primary}
    }
    &:active,
    &.open {
        background-color: var(--action-selected) !important;
        color: ${tokens.text.primary}
    }
`;

const StyledCorporationDropdown = styled(CorporationDropdown)`
    ${dropdownStyles}
`;

const StyledLocationDropdown = styled(LocationDropdown)`
    ${dropdownStyles}
`;

type EntityNavigationProps = {
    corporation: ICorporationResponse | undefined;
    location: ILocationResponse | undefined;
    isLoading: boolean;
    isSmallScreen: boolean;
};

const EntityNavigation = memo(
    ({ corporation, location, isLoading, isSmallScreen }: EntityNavigationProps) => {
        const user = useUser();

        const { pathname } = useLocation();

        const {
            determineCorporationRoute,
            navigateToCorporationPage,
            navigateToLocationPage,
        } = useScopeNavigation(user.user);

        const corporationRoute = useMemo(
            () => (corporation ? determineCorporationRoute(corporation) : undefined),
            // forcing change on url change
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [corporation, determineCorporationRoute, pathname],
        );

        return (
            <>
                {corporation && corporationRoute && (
                    <CorporationLogoButton
                        url={corporationRoute}
                        selectedCorporation={corporation}
                    />
                )}
                <StyledCorporationDropdown
                    value={corporation}
                    onChange={navigateToCorporationPage}
                    placeholder="Corporations"
                    popupWidth="40rem"
                    interactionMode="select"
                    renderLabel={isSmallScreen ? CorporationAbbreviation : undefined}
                />
                {corporation && (
                    <SpinnerArea isLoading={isLoading} hideChildren size="1x" noContainer>
                        <StyledLocationDropdown
                            showFavoriteIcon
                            corporationId={corporation.Id}
                            value={location}
                            onChange={navigateToLocationPage}
                            placeholder="Locations"
                            popupWidth="40rem"
                            interactionMode="link"
                            renderLabel={({ value: loc }) => (
                                <LocationDropdownLabel className="ellipses-1-line">
                                    {loc.Name}
                                </LocationDropdownLabel>
                            )}
                        />
                    </SpinnerArea>
                )}
            </>
        );
    },
);

export function TopBar() {
    const user = useUser();
    const { corporation, location, isLoading } = useRouteEntities();
    const { canViewGlobalSearchIcon } = usePermissions();
    const {
        isVisible: isGlobalSearchVisible,
        setIsVisible: setGlobalSearchVisible,
    } = useGlobalSearchVisibility();
    const rightTopBarSectionRef = useRef<HTMLDivElement>(null);

    const { globalUrl } = useScopeNavigation(user.user);

    const { toggleDrawerExpanded } = useNavigationDrawerValues((state) => ({
        toggleDrawerExpanded: state.toggleDrawerExpanded,
    }));

    const isSmallScreen = useIsViewportAtMost(MediaSize.small);

    const isDashboardView = useIsDashboardView();
    const isMissionControl = useIsMissionControl();
    const isVisible = !isDashboardView;
    const showMissionControlEntityNav = !isLoading && isMissionControl && !corporation;

    // auto-adjust the pendo badge position based on the space taken up by the
    // rightmost section of the top bar
    useResizeObserver(rightTopBarSectionRef, (e) => {
        document.body.style.setProperty(
            '--pendo-badge-inset-right',
            e.contentRect.width + 14 + 'px',
        );
    });
    useLayoutEffect(() => {
        if (rightTopBarSectionRef.current) {
            document.body.style.setProperty(
                '--pendo-badge-inset-right',
                rightTopBarSectionRef.current.clientWidth + 14 + 'px',
            );
        }
    }, [isVisible]);

    if (!isVisible) {
        return <></>;
    }

    return (
        <div>
            <GlobalSearch
                isVisible={isGlobalSearchVisible}
                onHide={() => setGlobalSearchVisible(false)}
            />
            <TopBarWrapper className="top-bar" aria-label="Primary navigation">
                <TopBarSection>
                    {isSmallScreen && (
                        <HoverIconButton
                            size="1.8rem"
                            aria-label="Expand"
                            title="Expand"
                            icon={faBars}
                            hoverIcon={faBars}
                            color="currentColor"
                            onClick={toggleDrawerExpanded}
                        />
                    )}
                    {hasAccessToMultipleCorporations(user.user) && (
                        <MenuLink
                            tabIndex={0}
                            $padding="1rem"
                            title="Go to global page"
                            aria-label="go to global page"
                            to={globalUrl}
                        >
                            <FontAwesomeIcon
                                className="f-18 cursor-pointer"
                                icon={faHome}
                            />
                        </MenuLink>
                    )}
                    {!isSmallScreen &&
                        (showMissionControlEntityNav ? (
                            <MissionControlEntityNav />
                        ) : (
                            <EntityNavigation
                                corporation={corporation}
                                location={location}
                                isLoading={isLoading}
                                isSmallScreen={isSmallScreen}
                            />
                        ))}
                    <EnvironmentSwitcherPopup />
                    <QaUserSwitcherPopup />
                </TopBarSection>
                <TopBarSection ref={rightTopBarSectionRef}>
                    {canViewGlobalSearchIcon && (
                        <MenuButton
                            aria-label="show or hide global search modal"
                            onClick={() => setGlobalSearchVisible(true)}
                            title={`Open Search (${ShortcutDescription})`}
                            $active={isGlobalSearchVisible}
                        >
                            <FontAwesomeIcon icon={faSearch} />
                        </MenuButton>
                    )}
                    <FavoritesPopup
                        corporationId={corporation?.Id}
                        locationId={location?.Id}
                    />
                    <UserPopup />
                    <TopBarThemePicker />
                    <DebugToggle />
                </TopBarSection>
            </TopBarWrapper>
            {isSmallScreen && (
                <TopBarWrapper className="flex-row secondary-top-bar align-center">
                    {showMissionControlEntityNav ? (
                        <MissionControlEntityNav />
                    ) : (
                        <EntityNavigation
                            corporation={corporation}
                            location={location}
                            isLoading={isLoading}
                            isSmallScreen={isSmallScreen}
                        />
                    )}
                </TopBarWrapper>
            )}
        </div>
    );
}
